import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  ArrowLeft,
  Edit,
  ExternalLink,
  FileText,
  History,
  MessageSquare,
  Paperclip,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ActivityLogTab } from "@features/ax/shared/ActivityLog";
import { NotesTab } from "@features/ax/shared/NotesTab";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { useToast } from "@shared/hooks/use-toast";
import { Badge } from "@shared/ui/badge";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { CreateServiceOrderModal } from "../../../../shared/serviceOrderModal";
import { useFailuresDetails, useIgnoreFailure } from "../../hooks";
import { FailureDetailData, StatusHistoryEntry } from "../../types";
import { AttachmentsTab } from "./attachmentsTab";

const FailureDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();
  const { data: failureDetailsData } = useFailuresDetails({
    remote_utility_id: remoteUtilityId,
    id: id,
  });
  const ignoreFailureMutation = useIgnoreFailure();
  const [isIgnoreDialogOpen, setIsIgnoreDialogOpen] = useState(false); // Add this line here
  const [isCreateSOModalOpen, setIsCreateSOModalOpen] = useState(false);
  const [isCreatingServiceOrder, setIsCreatingServiceOrder] = useState(false);
  // Transform API data to match component interface
  const handleIgnoreFailureClick = () => {
    setIsIgnoreDialogOpen(true);
  };

  const handleCreateServiceOrderClick = () => {
    setIsCreateSOModalOpen(true);
  };

  const handleCreateServiceOrder = async (data: any) => {
    setIsCreatingServiceOrder(true);
    try {
      toast({
        title: "Success",
        description: "Service order created successfully",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create service order",
        variant: "destructive",
      });
    } finally {
      setIsCreatingServiceOrder(false);
    }
  };

  const handleConfirmIgnoreFailure = () => {
    if (!failure) return;

    ignoreFailureMutation.mutate(
      {
        id: id,
        payload: {
          status: 6,
          reject_remark: "ignored",
        },
      },
      {
        onSuccess: (data, variables) => {
          toast({
            title: "Success",
            description: "Failure ignored successfully",
            variant: "default",
          });
          queryClient.removeQueries({
            queryKey: QueryKeyFactory.module.ax.failures.failuresDetails({
              remote_utility_id: remoteUtilityId,
              id: variables.id,
            }),
          });
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.failures
              .failuresList({
                remote_utility_id: remoteUtilityId,
              })
              .slice(0, 2),
          });
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: "Failed to ignore failure",
            variant: "destructive",
          });
        },
        onSettled: () => {
          setIsIgnoreDialogOpen(false);
        },
      }
    );
  };

  const handleCloseIgnoreDialog = () => {
    setIsIgnoreDialogOpen(false);
  };
  const failure: FailureDetailData | null = useMemo(() => {
    if (!failureDetailsData) return null;

    const data = failureDetailsData;

    // Parse observed symptoms
    let symptoms: string[] = [];
    try {
      if (data.observedSymptoms && data.observedSymptoms.length > 0) {
        const parsed = JSON.parse(data.observedSymptoms[0]);
        symptoms = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      symptoms = [];
    }

    // Transform attachments from documents
    const attachments = (data.documents || []).map(
      (doc: any, index: number) => ({
        id: doc.id?.toString() || index.toString(),
        name: doc.name || "NA",
        type: doc.type === "image" ? ("image" as const) : ("document" as const),
        url: doc.url || "#",
        uploadedAt: doc.uploadedAt || "NA",
      })
    );

    // Create mock status history since it's not in API response
    const statusHistory: StatusHistoryEntry[] = [
      {
        id: "1",
        dateTime: data.failureAriseDate || "NA",
        event: "Reported",
        details: "Initial failure report submitted.",
        performedBy: "System",
      },
      {
        id: "2",
        dateTime: data.failureAriseDate || "NA",
        event: "Status Change",
        details: `Status set to ${data.statusDisplay || "NA"}`,
        performedBy: "System",
      },
    ];

    // Get linked service orders - map all of them, not just the first one
    const linkedServiceOrders = (data.linkedServiceOrders || []).map(
      (order: any) => ({
        id: order.serviceOrderId || "NA",
        name: order.serviceOrderName || "NA",
        priority: order.soPriority || "NA",
        status: order.soStatus || "NA",
        assignedTo: order.assignedTo || "NA",
      })
    );

    // For backward compatibility, keep the single linkedServiceOrder as the first one
    const linkedServiceOrder =
      linkedServiceOrders.length > 0 ? linkedServiceOrders[0] : undefined;

    return {
      id: data.failureNo || "NA",
      assetId: data.asset?.assetNo?.toString() || "NA",
      assetName: data.asset?.assetName?.toString() || "NA",
      assetType: data.asset?.assetTypeDisplay?.toString() || "NA",
      location: data.asset?.location?.toString() || "NA",
      specificLocation: data.specificLocationDetails || "NA",
      dateReported: data.failureAriseDate || "NA",
      reportedBy: data?.createdUserRemoteName || "NA",
      dateOfFailure: data.failureAriseDate || "NA",
      failureMode: data.failureModeDisplay || "NA",
      symptoms: symptoms,
      description: data.description || "NA",
      additionalNotes: data.additionalNotes || "NA",
      priority: data.priorityDisplay?.includes("Critical")
        ? "Critical"
        : data.priorityDisplay?.includes("High")
        ? "High"
        : data.priorityDisplay?.includes("Medium")
        ? "Medium"
        : data.priorityDisplay?.includes("Low")
        ? "Low"
        : "Medium",
      status: data.statusDisplay,
      impactOfFailure: data.serviceImpactAssessment
        ? [data.serviceImpactAssessment]
        : [],
      linkedServiceOrder: linkedServiceOrder,
      statusHistory: statusHistory,
      attachments: attachments,
      assetTypeDisplay: data.assetTypeDisplay || "",
    };
  }, [failureDetailsData]);

  // Create tab components with stable references - MUST be before early return
  const tabComponents = useMemo(() => {
    if (!failure) return {};

    return {
      attachments: {
        label: "Photos & Attachments",
        icon: <Paperclip className="h-4 w-4" />,
        component: (
          <AttachmentsTab
            key="attachments"
            failure={failure}
            documents={failureDetailsData?.documents || []}
          />
        ),
      },
      notes: {
        label: "Notes",
        icon: <MessageSquare className="h-4 w-4" />,
        component: (
          <NotesTab key="notes" requestId={id} source={"3"} title="Failure" />
        ),
      },
      "status-history": {
        label: "Activity Log",
        icon: <History className="h-4 w-4" />,
        component: <ActivityLogTab entityId={id} entityType={"3"} />,
      },
    };
  }, [failure, failureDetailsData]);

  // ADD THIS NULL CHECK - This is the fix!
  if (!failure) {
    return (
      <div className="p-6 space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/ax/field-alerts/failure")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Failures List
        </Button>
      </div>
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "bg-red-100 text-red-800 border-red-200";
      case "High":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "In Progress":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "Pending Action":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Service Order Created":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "Ignored":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "Resolved":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="p-2 space-y-6">
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="flex items-center gap-2">
              <ArrowLeft
                className="w-4 h-4 cursor-pointer text-gray-700"
                onClick={() => navigate(-1)}
              />
              <CardTitle className="flex justify-between items-center w-full">
                <span>Failure Details</span>
                <Badge className="text-xs">{failure.status}</Badge>{" "}
              </CardTitle>
            </div>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ValueLabel label="Failure ID" value={failure.id} />
                <ValueLabel label="Asset ID" value={failure.assetId} />
                <ValueLabel
                  label="Asset Type"
                  value={failure.assetTypeDisplay}
                />
                <ValueLabel label="Location" value={failure.location} />

                <ValueLabel
                  label="Date Reported"
                  value={failure.dateReported}
                />

                <ValueLabel label="Reported By" value={failure.reportedBy} />

                <ValueLabel
                  label="Date of Failure (Approx.)"
                  value={failure.dateOfFailure}
                />

                <ValueLabel label="Failure Mode" value={failure.failureMode} />

                <ValueLabel
                  label="Observed Symptoms"
                  value={
                    failure.symptoms.length > 0
                      ? failure.symptoms.join(", ")
                      : "NA"
                  }
                />
              </div>

              <ValueLabel
                label="Failure Description"
                value={failure.description}
              />

              <ValueLabel
                label="Additional Notes"
                value={failure.additionalNotes}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Quick Actions */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="mb-2">
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() =>
                  navigate(`/ax/field-alerts/failure/report/edit/${id}`)
                }
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Failure Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleCreateServiceOrderClick}
                tooltip={
                  failureDetailsData?.statusDisplay === "Service Order Created"
                    ? "Service order is created for this failure."
                    : null
                }
                disabled={
                  failureDetailsData?.statusDisplay === "Service Order Created"
                }
              >
                <FileText className="w-4 h-4 mr-2" />
                Create Service Order
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleIgnoreFailureClick}
                disabled={failure.status === "Ignored"}
              >
                <X className="w-4 h-4 mr-2" />
                Ignore Failure
              </Button>
            </CardContent>
          </Card>

          {/* Linked Service Order */}
          {failure.linkedServiceOrder && (
            <Card>
              <CardHeader>
                <CardTitle>Linked Service Order</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <ValueLabel
                    label="Service Order ID"
                    value={failure.linkedServiceOrder.id}
                  />
                  <ValueLabel
                    label="Service Order Name"
                    value={failure.linkedServiceOrder.name}
                  />
                  <ValueLabel
                    label="SO Priority"
                    value={failure.linkedServiceOrder.priority}
                  />
                  <ValueLabel
                    label="SO Status"
                    value={failure.linkedServiceOrder.status}
                  />
                  <ValueLabel
                    label="Assigned To"
                    value={failure.linkedServiceOrder.assignedTo}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start mt-4"
                  >
                    <ExternalLink />
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Bottom Tabs using Tab Service */}
      <Tabs
        defaultValue="attachments"
        className="space-y-4"
        urlMapping={{
          "status-history": "status-history",
          attachments: "attachments",
        }}
        disableUrlSync={false}
        idPrefix="failure-detail"
        tabComponents={tabComponents}
        tabsListClassName="grid w-full grid-cols-3 p-1"
      />
      <DeleteConfirmationDialog
        isOpen={isIgnoreDialogOpen}
        onClose={handleCloseIgnoreDialog}
        onConfirm={handleConfirmIgnoreFailure}
        title="Ignore Failure"
        itemName={failure?.id}
        description={`Are you sure you want to ignore failure "${failure?.id}"? This will mark the failure as ignored and remove it from active tracking.`}
        confirmText="Ignore Failure"
        isLoading={ignoreFailureMutation.isPending}
      />
      <CreateServiceOrderModal
        isOpen={isCreateSOModalOpen}
        onClose={() => setIsCreateSOModalOpen(false)}
        onSubmit={handleCreateServiceOrder}
        failureId={id}
        requestDetails={failureDetailsData}
      />
    </div>
  );
};

export default FailureDetail;
